import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import RaceButton from '@/components/elements/Button/RaceButton';
import RaceProgramDetail from '@/components/layouts/Race/Singlerace/RaceProgramInfo/RaceProgramDetail';
import ResultUI, { ResultsTemplateProps } from '@/components/templates/Results';
import { RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { raceSelector } from '@/selectors/race';
import { toHalfWidth } from '@/utility/utils';

import { NoRaceText, RaceProgramListWrapper } from '../style';
import {
  InfoWarningIcon,
  LinkWrapper,
  List,
  ListWrapper,
  ProgramName,
  RaceProgramInfoWrapper,
  StopHeldText,
} from './style';

interface SingleraceResultsTemplateProps extends ResultsTemplateProps {
  racePrograms: RaceProgram[];
  loading: boolean;
  getCommentCount: (raceId: string) => boolean;
}

const SingleraceResultsTemplate: React.FC<SingleraceResultsTemplateProps> = ({
  date,
  raceType,
  setRaceType,
  racePrograms,
  loading,
  getCommentCount,
}) => {
  const getRaceFlags = (
    rp: RaceProgram,
    raceNum: number,
  ): {
    isPast: boolean;
    isCanceled: boolean;
    isFuture: boolean;
    to: string;
  } => {
    const race = rp.races.find(r => r.raceNum === raceNum);
    if (!race) {
      return {
        isPast: false,
        isCanceled: false,
        isFuture: true,
        to: '',
      };
    }

    let linkString = `/${
      getVelodromeByCode(rp.velodromeCode).romanName
    }/race/${rp.heldDate.format('YYYY-MM-DD')}/${race.raceNum}`;

    if (race.singleRaceRefunds.length !== 0) {
      linkString = linkString + '?infoType=result';
    }
    return {
      to: linkString,
      isPast: rp.isCanceled || race.singleRaceRefunds.length !== 0,
      isCanceled: rp.isCanceled || raceSelector(race).isCanceled,
      isFuture: false,
    };
  };

  return (
    <ResultUI date={date} raceType={raceType} setRaceType={setRaceType}>
      <RaceProgramListWrapper isShow={racePrograms.length > 0}>
        <ListWrapper component="ul">
          {racePrograms.map(rp => (
            <CSSTransition key={rp.id} timeout={200}>
              <List>
                <RaceProgramInfoWrapper>
                  <RaceProgramDetail
                    raceProgram={rp}
                    hasLink={!rp.isCanceled}
                    href={`/${
                      getVelodromeByCode(rp.velodromeCode).romanName
                    }/race/${rp.heldDate.format('YYYY-MM-DD')}/result`}
                  />
                  {rp.isCanceled && (
                    <StopHeldText>
                      <InfoWarningIcon />
                      レースは中止になりました
                    </StopHeldText>
                  )}
                  <ProgramName>{toHalfWidth(rp.programName)}</ProgramName>
                </RaceProgramInfoWrapper>
                <LinkWrapper>
                  {[...Array(rp.raceCount)].map((_, i) => (
                    <RaceButton
                      key={`race-${rp.id}-${i}`}
                      raceNum={i + 1}
                      {...getRaceFlags(rp, i + 1)}
                      raceId={rp.races[i]?.id}
                      getCommentCount={getCommentCount}
                    />
                  ))}
                </LinkWrapper>
              </List>
            </CSSTransition>
          ))}
        </ListWrapper>
      </RaceProgramListWrapper>
      <CSSTransition
        in={racePrograms.length === 0 && !loading}
        timeout={200}
        unmountOnExit
      >
        <NoRaceText>開催情報がありません</NoRaceText>
      </CSSTransition>
    </ResultUI>
  );
};

export default SingleraceResultsTemplate;
